/**
 * Health Intelligence Insight Generator
 * 
 * Computes a single primary insight from today's macro totals vs goals.
 * Pure frontend logic — no backend dependency for v1.
 */

export const INSIGHT_TYPES = {
  PROTEIN_LOW: 'protein_low',
  FAT_HIGH: 'fat_high',
  CALORIES_HIGH: 'calories_high',
  CARBS_HIGH: 'carbs_high',
  ON_TRACK: 'on_track',
  NO_DATA: 'no_data'
};

/**
 * Generate a single health intelligence insight from today's data
 * @param {object} params
 * @param {number} params.calories - Today's calories consumed
 * @param {number} params.calorieGoal - Daily calorie target
 * @param {number} params.protein - Today's protein (g)
 * @param {number} params.proteinGoal - Protein target (g)
 * @param {number} params.fat - Today's fat (g)
 * @param {number} params.fatGoal - Fat target (g)
 * @param {number} params.carbs - Today's carbs (g)
 * @param {number} params.carbsGoal - Carbs target (g)
 * @returns {object} insight - { type, headline, reason, fixRoute, fixFilter, askWhyPrefill }
 */
export function generateInsight({ calories = 0, calorieGoal = 2000, protein = 0, proteinGoal = 150, fat = 0, fatGoal = 65, carbs = 0, carbsGoal = 250 }) {
  // No data yet
  if (calories === 0 && protein === 0 && fat === 0) {
    return {
      type: INSIGHT_TYPES.NO_DATA,
      headline: 'Log a meal to get your first insight',
      reason: 'Health Intelligence activates after your first meal.',
      fixRoute: null,
      fixFilter: null,
      askWhyPrefill: 'What should I eat for my first meal today?'
    };
  }

  const proteinPct = proteinGoal > 0 ? (protein / proteinGoal) * 100 : 100;
  const fatPct = fatGoal > 0 ? (fat / fatGoal) * 100 : 0;
  const calPct = calorieGoal > 0 ? (calories / calorieGoal) * 100 : 0;
  const carbsPct = carbsGoal > 0 ? (carbs / carbsGoal) * 100 : 0;
  const proteinShort = Math.round(proteinGoal - protein);

  // Priority: protein low > fat high > calories high > carbs high > on track
  if (proteinPct < 80) {
    return {
      type: INSIGHT_TYPES.PROTEIN_LOW,
      headline: 'Protein is low today',
      reason: `You're ~${proteinShort}g short of your ${proteinGoal}g target.`,
      fixRoute: 'smart_meals',
      fixFilter: 'high_protein',
      askWhyPrefill: 'Why is my protein low today and what\'s the easiest fix?'
    };
  }

  if (fatPct > 110) {
    const fatOver = Math.round(fat - fatGoal);
    return {
      type: INSIGHT_TYPES.FAT_HIGH,
      headline: 'Fats trending high',
      reason: `You're ${fatOver}g over your ${fatGoal}g goal.`,
      fixRoute: 'smart_swaps',
      fixFilter: 'lower_fat',
      askWhyPrefill: 'Why is my fat intake high today and what should I change?'
    };
  }

  if (calPct > 110) {
    const calOver = Math.round(calories - calorieGoal);
    return {
      type: INSIGHT_TYPES.CALORIES_HIGH,
      headline: 'Calories running high',
      reason: `You're ${calOver} kcal over your ${calorieGoal} target.`,
      fixRoute: 'smart_swaps',
      fixFilter: 'lower_calorie',
      askWhyPrefill: 'I\'m over my calorie target today. What should I do for my next meal?'
    };
  }

  if (carbsPct > 110) {
    const carbsOver = Math.round(carbs - carbsGoal);
    return {
      type: INSIGHT_TYPES.CARBS_HIGH,
      headline: 'Carbs trending high',
      reason: `You're ${carbsOver}g over your ${carbsGoal}g target.`,
      fixRoute: 'smart_swaps',
      fixFilter: 'lower_carb',
      askWhyPrefill: 'Why are my carbs high today and what\'s a good swap?'
    };
  }

  return {
    type: INSIGHT_TYPES.ON_TRACK,
    headline: 'On track today',
    reason: 'Keep going — your plan is working.',
    fixRoute: 'smart_meals',
    fixFilter: 'stay_on_track',
    askWhyPrefill: 'What should I do to stay on track for the rest of the day?'
  };
}

/**
 * Get the icon for an insight type
 */
export function getInsightIcon(type) {
  switch (type) {
    case INSIGHT_TYPES.ON_TRACK: return '✅';
    case INSIGHT_TYPES.NO_DATA: return '💡';
    default: return '⚠️';
  }
}

/**
 * Determine Ask Why daily limit based on tier and trial status
 */
export function getAskWhyLimit(userTier, isTrial) {
  if (isTrial) return Infinity;
  switch (userTier) {
    case 'miq':
    case 'genius': return Infinity;
    case 'enhanced':
    case 'smart': return 5;
    default: return 3;
  }
}

/**
 * Determine Fix This access level
 * @returns 'full' | 'limited' | 'upsell'
 */
export function getFixThisAccess(userTier, isTrial) {
  if (isTrial) return 'full';
  switch (userTier) {
    case 'miq':
    case 'genius': return 'full';
    case 'enhanced':
    case 'smart': return 'limited';
    default: return 'upsell';
  }
}

/**
 * Get a limited/free suggestion for "Fix This" when user doesn't have full access
 */
export function getLimitedSuggestion(insightType) {
  switch (insightType) {
    case INSIGHT_TYPES.PROTEIN_LOW:
      return 'Try adding a lean protein snack — like Greek yogurt, chicken breast, or a protein shake.';
    case INSIGHT_TYPES.FAT_HIGH:
      return 'Go lighter on oils and dressings for your next meal.';
    case INSIGHT_TYPES.CALORIES_HIGH:
      return 'Consider a lighter next meal — salad with lean protein works well.';
    case INSIGHT_TYPES.CARBS_HIGH:
      return 'Swap starchy sides for vegetables at your next meal.';
    case INSIGHT_TYPES.ON_TRACK:
      return 'You\'re doing great! Keep your next meal balanced.';
    default:
      return 'Log a meal to unlock personalized suggestions.';
  }
}
